import { type ButtonProps, buttonVariants, Button as HeroButton } from "@heroui/react";
import { tv, type VariantProps } from "tailwind-variants";
import { Lib } from "..";

const newButtonVariants: ReturnType<typeof tv> = tv(
    {
        extend: buttonVariants,
        base: [
            "h-auto",
            // Typography - Font Family
            "font-button-text",
            "font-weight-button",
            "text-mobile-button-medium",
            "sm-md:text-desktop-button-medium",
            // Typography - Line Height (Mobile default, Desktop at md)
            "leading-mobile-button-medium",
            "sm-md:leading-desktop-button-medium",
            // Typography - Letter Spacing
            "tracking-button",
            // Typography - Text Transform
            "text-case-button",
            // Border
            "border",
            // Other
            "transition-all",
            "disabled:cursor-not-allowed",
            "gap-12",
            "items-center",
            "align-center",
        ],
        variants: {
            variant: {
                primary: [
                    // Background
                    "bg-component-button-primary-fill",
                    "hover:bg-component-button-primary-hover-fill",
                    "active:bg-component-button-primary-pressed-fill",
                    "disabled:bg-component-button-primary-inactive-fill",
                    // Text
                    "text-component-button-primary-text",
                    "hover:text-component-button-primary-hover-text",
                    "active:text-component-button-primary-pressed-text",
                    "disabled:text-component-button-primary-inactive-text",
                    // Border
                    "border-component-button-primary-outline",
                    "hover:border-component-button-primary-hover-outline",
                    "active:border-component-button-primary-pressed-outline",
                    "disabled:border-component-button-primary-inactive-outline",
                    // Border Radius
                    "rounded-button",
                    // Spacing - Padding (Mobile default, Desktop at md)
                    "px-32",
                    "py-16",
                ],
                secondary: [
                    // Background
                    "bg-component-button-secondary-fill",
                    "hover:bg-component-button-secondary-hover-fill",
                    "active:bg-component-button-secondary-pressed-fill",
                    "disabled:bg-component-button-secondary-inactive-fill",
                    // Text
                    "text-component-button-secondary-text",
                    "hover:text-component-button-secondary-hover-text",
                    "active:text-component-button-secondary-pressed-text",
                    "disabled:text-component-button-secondary-inactive-text",
                    // Border
                    "border-component-button-secondary-outline",
                    "hover:border-component-button-secondary-hover-outline",
                    "active:border-component-button-secondary-pressed-outline",
                    "disabled:border-component-button-secondary-inactive-outline",
                    // Border Radius
                    "rounded-button",
                    // Spacing - Padding (Mobile default, Desktop at md)
                    "px-24",
                    "py-12",
                ],
                onboarding: [
                    // Background
                    "bg-component-button-onboarding-fill",

                    "hover:bg-component-button-onboarding-hover-fill",
                    "active:bg-component-button-onboarding-pressed-fill",
                    "disabled:bg-component-button-onboarding-inactive-fill",
                    // Text
                    "text-component-button-onboarding-text",
                    "hover:text-component-button-onboarding-hover-text",
                    "active:text-component-button-onboarding-pressed-text",
                    "disabled:text-component-button-onboarding-inactive-text",
                    // Border
                    "border-component-button-onboarding-outline",
                    "hover:border-component-button-onboarding-hover-outline",
                    "active:border-component-button-onboarding-pressed-outline",
                    "disabled:border-component-button-onboarding-inactive-outline",
                    // Border Radius
                    "rounded-rounded",
                    "rounded-br-sharp",
                    // Spacing - Padding (Mobile default, Desktop at md)
                    "p-[20px]",
                    // TODO: we want onboarding button to have a fixed height so it always looks the same
                    // and the font size won't affect it. It's hardcoded here until we add button height tokens
                    // or such to our design system.
                    "!h-[56px]",
                    // Spacing - Width
                    "w-full",
                    // Typography - Text Transform
                    "font-label",
                    "font-weight-label-medium",
                    "text-mobile-button-text",
                    "sm-md:text-desktop-button-text",
                    "normal-case",
                    "shadow-sm",
                    "active:shadow-none",
                ],
                inverse: [
                    // Background
                    "bg-component-button-inverse-fill",
                    "hover:bg-component-button-inverse-hover-fill",
                    "active:bg-component-button-inverse-pressed-fill",
                    "disabled:bg-component-button-inverse-inactive-fill",
                    // Text
                    "text-component-button-inverse-text",
                    "hover:text-component-button-inverse-hover-text",
                    "active:text-component-button-inverse-pressed-text",
                    "disabled:text-component-button-inverse-inactive-text",
                    // Border
                    "border-component-button-inverse-outline",
                    "hover:border-component-button-inverse-hover-outline",
                    "active:border-component-button-inverse-pressed-outline",
                    "disabled:border-component-button-inverse-inactive-outline",
                    // Border Radius
                    "rounded-button",
                    // Spacing - Padding (Mobile default, Desktop at md)
                    "px-32",
                    "py-16",
                    // Typography
                    "text-mobile-button-small",
                    "sm-md:text-desktop-button-small",
                ],
                filter: [
                    // Background
                    "bg-component-button-filter-unselected-fill",
                    "hover:bg-component-button-filter-hover-fill",
                    "active:bg-component-button-filter-pressed-fill",
                    // Text
                    "text-component-button-filter-unselected-text",
                    "hover:text-component-button-filter-hover-text",
                    "active:text-component-button-filter-pressed-text",
                    // Border
                    "border-component-button-filter-unselected-outline",
                    "hover:border-component-button-filter-hover-outline",
                    // Border Radius
                    "rounded-full",
                    // Spacing - Padding (Mobile default, Desktop at md)
                    "px-16",
                    "py-12",
                    "sm-md:px-24",
                    "md:py-16",
                    // Typography - Text Transform
                    "normal-case",
                    "text-mobile-button-text",
                    "sm-md:text-desktop-button-text",
                ],
                filterWithoutIcon: [
                    // Background
                    "bg-component-button-filter-unselected-fill",
                    "hover:bg-component-button-filter-hover-fill",
                    "active:bg-component-button-filter-pressed-fill",
                    // Text
                    "text-component-button-filter-unselected-text",
                    "hover:text-component-button-filter-hover-text",
                    "active:text-component-button-filter-pressed-text",
                    // Border
                    "border-component-button-filter-unselected-outline",
                    "hover:border-component-button-filter-hover-outline",
                    // Border Radius
                    "rounded-full",
                    // Spacing - Padding (Mobile default, Desktop at md)
                    "px-16",
                    "py-12",
                    "md:px-24",
                    "md:py-16",
                    // Typography - Text Transform
                    "normal-case",
                    "text-mobile-button-text",
                    "md:text-desktop-button-text",
                ],
            },
            selected: {
                true: [
                    "bg-component-button-filter-selected-fill!",
                    "text-component-button-filter-selected-text!",
                    "border-component-button-filter-selected-outline!",
                ],
            },
        },
        defaultVariants: {
            variant: "primary",
        },
        // @ts-expect-error TS2322: compound variant boolean not accepted when extend+ReturnType<typeof tv> annotation (TS2742 fix)
        compoundVariants: [{ variant: "filter", selected: true }],
    },
    {
        twMerge: false,
    },
);

export type NewButtonVariants = VariantProps<typeof newButtonVariants>;
interface NewButtonProps
    extends Omit<ButtonProps, "className" | "variant" | "children">,
        NewButtonVariants {
    className?: string;
    selected?: boolean;
    children?: React.ReactNode;
}

export function NewButton({
    variant,
    className,
    selected,
    children,
    ...props
}: NewButtonProps) {
    return (
        <HeroButton
            className={newButtonVariants({ variant, className, selected })}
            {...props}
        >
            <span className="flex items-center justify-center gap-4 truncate pb-2">
                {children}
            </span>
            {variant === "filter" && selected && (
                <Lib.Icon.Close.Small className="!leading-mobile-button-medium md:!leading-desktop-button-medium translate-y-2" />
            )}
        </HeroButton>
    );
}
